import { useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Spinner } from '@radix-ui/themes';
import { useAuth } from '../../context/AuthContext';
import { BrandDot } from './BrandDot';
import { EchoLogo } from './EchoLogo';
import { HeaderNav } from './HeaderNav';
import { HeaderSearchBar } from './HeaderSearchBar';
import { UserAvatarMenu } from './UserAvatarMenu';
import { NotificationPopover } from '../notification/NotificationPopover';
import { MegaMenuPanel } from './MegaMenuPanel';

export function AppHeader() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHomepage = pathname === '/';
  const pillRef = useRef<HTMLDivElement>(null);
  // Pill hover-intent: only show background after sustained hover (150ms),
  // so a cursor passing through doesn't briefly flash the background.
  const [pillHovered, setPillHovered] = useState(false);
  const pillHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Homepage-only: pill + search backgrounds become visible as soon as the
  // user starts scrolling so the chrome doesn't fight the hero's content
  // for legibility. Snaps back to transparent only when at the very top.
  const [scrolledPastFold, setScrolledPastFold] = useState(false);
  useEffect(() => {
    if (!isHomepage) {
      setScrolledPastFold(false);
      return;
    }
    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      // 8px threshold (instead of 0) avoids flicker from scroll-bounce
      // and small touchpad noise while at the top of the page.
      setScrolledPastFold(window.scrollY > 8);
    };
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(update);
    };
    update(); // initial
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isHomepage]);

  const handlePillEnter = useCallback(() => {
    if (pillHoverTimerRef.current) clearTimeout(pillHoverTimerRef.current);
    pillHoverTimerRef.current = setTimeout(() => {
      setPillHovered(true);
      pillHoverTimerRef.current = null;
    }, 150);
  }, []);

  const handlePillLeave = useCallback(() => {
    if (pillHoverTimerRef.current) {
      clearTimeout(pillHoverTimerRef.current);
      pillHoverTimerRef.current = null;
    }
    setPillHovered(false);
  }, []);

  // Mega-menu hover state (lifted from old HeaderNav)
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [closingMenu, setClosingMenu] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closedAtRef = useRef(0);

  const handleMenuEnter = useCallback((menu: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Cancel closing animation if re-entering
    if (closingTimerRef.current) {
      clearTimeout(closingTimerRef.current);
      closingTimerRef.current = undefined;
      setClosingMenu(null);
    }
    // Ignore phantom mouseenter caused by portal unmounting
    if (Date.now() - closedAtRef.current < 150) return;
    // Hover-intent delay: wait briefly before opening so a mouse swept
    // across the nav doesn't pop every menu in turn. Cancelled by leave.
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      setOpenMenu(menu);
      openTimerRef.current = null;
    }, 120);
  }, []);

  const handleMenuLeave = useCallback(() => {
    // Cancel any pending open if the user leaves before the delay fires.
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    closeTimerRef.current = setTimeout(() => {
      closedAtRef.current = Date.now();
      setOpenMenu((prev) => {
        if (prev) {
          setClosingMenu(prev);
          clearTimeout(closingTimerRef.current);
          closingTimerRef.current = setTimeout(() => setClosingMenu(null), 460);
        }
        return null;
      });
    }, 150);
  }, []);

  const closeMenuAnimated = useCallback(() => {
    closedAtRef.current = Date.now();
    setOpenMenu((prev) => {
      if (prev) {
        setClosingMenu(prev);
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = setTimeout(() => setClosingMenu(null), 460);
      }
      return null;
    });
  }, []);

  // Close mega-menu on Escape
  useEffect(() => {
    if (!openMenu) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenuAnimated();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openMenu, closeMenuAnimated]);

  // Close mega-menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handleClick = (e: MouseEvent) => {
      const wrapper = pillRef.current;
      if (wrapper && !wrapper.contains(e.target as Node)) {
        closeMenuAnimated();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenu, closeMenuAnimated]);

  const handleCategoryClick = useCallback(
    (sectionName: string, category: { id: string; name: string }) => {
      closeMenuAnimated();
      // Spec 022 — Assets is its own top-level surface (matches the
      // navbar tab behaviour). Asset library filters by categoryId
      // (uuid), guideline library filters by category name. Branch
      // on sectionName so each surface gets the URL shape it expects.
      if (sectionName === 'assets') {
        navigate(`/library/assets?categoryId=${encodeURIComponent(category.id)}`);
        return;
      }
      navigate(
        `/library?section=${sectionName}&category=${encodeURIComponent(category.name)}`
      );
    },
    [navigate, closeMenuAnimated]
  );

  // One shared gate for the unified container's background + bottom
  // stroke: transparent over the homepage hero until the user scrolls,
  // hovers the chrome, or opens a mega-menu.
  const chromeVisible =
    !isHomepage || scrolledPastFold || pillHovered || !!openMenu || !!closingMenu;

  // Mega-menu width — capped so its right edge stops 16px before the
  // right cluster (the search bar), leaving a clean gap to the search
  // tray. Measured because the cluster's x shifts with viewport width.
  const clusterRef = useRef<HTMLDivElement>(null);
  const [megaMenuMaxWidth, setMegaMenuMaxWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!openMenu && !closingMenu) return;
    const measure = () => {
      const container = pillRef.current;
      const cluster = clusterRef.current;
      if (!container || !cluster) return;
      const width =
        cluster.getBoundingClientRect().left -
        container.getBoundingClientRect().left -
        16;
      setMegaMenuMaxWidth(Math.max(0, width));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [openMenu, closingMenu]);

  return (
    <header className={`sticky top-0 z-50 ${isHomepage ? '' : 'bg-[var(--color-background)]'}`}>
      <div className="flex h-20 items-center px-6 relative">
        {/* Unified header container — a transparent clip/layout box.
            The white surface lives on the nav row (full width) and the
            mega-menu panel (capped width) so it never bleeds to the
            right of the panel. */}
        <div
          ref={pillRef}
          style={{
            boxSizing: 'border-box',
            borderRadius: '8px',
            marginTop: '12px',
          }}
          className="overflow-hidden self-start z-50 flex-1 min-w-0"
          onMouseEnter={handlePillEnter}
          onMouseLeave={() => {
            handlePillLeave();
            handleMenuLeave();
          }}
        >
          {/* Top bar — single 56px row. White surface + bottom stroke
              run the full width; the stroke carries the nav's animated
              indicator. On phone-width viewports (<768px) the nav and
              right cluster are hidden and the logo is centred — see
              the `hidden md:flex` siblings and `justify-center
              md:justify-start` here. */}
          <div
            className="flex items-center h-14 px-4 md:pl-4 md:pr-2 gap-4 justify-center md:justify-start"
            style={{
              backgroundColor: chromeVisible ? 'var(--color-background)' : 'transparent',
              borderBottom: `1px solid ${chromeVisible ? 'var(--gray-6)' : 'transparent'}`,
              transition: 'background-color 250ms ease-out, border-color 250ms ease-out',
            }}
          >
            <Link to="/" className="flex items-center shrink-0 gap-2">
              <BrandDot style={{ marginRight: '8px' }} />
              <EchoLogo />
              <span
                className="text-[10px] whitespace-nowrap self-end mb-[1px]"
                style={{
                  color: 'var(--gray-9)',
                  marginTop: '10px',
                  fontFamily:
                    "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                V 1.0.0
              </span>
            </Link>

            {/* HeaderNav is wrapped so the visibility rule lives on a
                neutral div instead of fighting with HeaderNav's own
                hard-coded `flex` on its <nav> element. We use
                `max-md:hidden` (only `display:none` BELOW 768px)
                rather than `hidden md:flex`, so at desktop widths the
                natural `flex` simply applies — no two display rules
                arguing in the cascade.
                `h-full` on the wrapper is critical: the nav inside
                uses `h-full` itself to drive its bottom-aligned
                active-section indicator (sits at `bottom: -1px` of
                the nav, overlapping the header's bottom stroke).
                Without a height-defined wrapper the nav's h-full
                collapses to its content height and the indicator
                floats up under the label instead of on the stroke. */}
            <div className="max-md:hidden flex flex-1 justify-center min-w-0 h-full">
              <HeaderNav
                openMenu={openMenu}
                onMenuEnter={handleMenuEnter}
              />
            </div>

            {/* Right cluster — inside the shared container so the bottom
                stroke runs continuously beneath it. Hidden below the
                `md` breakpoint along with the nav, via the same
                `max-md:hidden` idiom (one rule, one breakpoint). */}
            <div ref={clusterRef} className="max-md:hidden flex items-center gap-4 shrink-0">
              {/* Functional search bar on every route — the search
                  endpoint is public, so logged-out users get it too. */}
              <div style={{ width: '200px' }}>
                <HeaderSearchBar />
              </div>
              {isAuthenticated ? (
                <>
                  <NotificationPopover />
                  <UserAvatarMenu />
                </>
              ) : isLoading ? (
                <Spinner size="2" />
              ) : (
                <>
                  <Button
                    variant="solid"
                    size="2"
                    className="!h-[42px] !px-6"
                    style={{ borderRadius: '8px', backgroundColor: '#4A4A4A', color: 'white', borderColor: '#4A4A4A' }}
                    onClick={() => navigate('/login')}
                  >
                    Report Drift
                  </Button>
                  <Button
                    variant="outline"
                    size="2"
                    className="!h-[42px] !px-6"
                    style={{ borderRadius: '8px', backgroundColor: 'white', color: '#1D1B1B', borderColor: '#1D1B1B' }}
                    onClick={() => navigate('/login')}
                  >
                    Propose Changes
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Mega-menu — grid-rows reveal animates the real content height
              paired with opacity + a small translateY so the panel eases
              in instead of snapping. Width-capped + left-anchored so it
              sits under logo+nav, not the right cluster. */}
          <div
            style={{
              display: 'grid',
              gridTemplateRows: openMenu ? '1fr' : '0fr',
              opacity: openMenu ? 1 : 0,
              transform: openMenu ? 'translateY(0)' : 'translateY(-6px)',
              transition:
                'grid-template-rows 460ms cubic-bezier(0.22, 1, 0.36, 1), ' +
                'opacity 280ms ease-out, ' +
                'transform 460ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <div style={{ overflow: 'hidden', minHeight: 0 }}>
              {(openMenu || closingMenu) && (
                <div style={{ maxWidth: megaMenuMaxWidth != null ? `${megaMenuMaxWidth}px` : '880px' }}>
                  <MegaMenuPanel
                    section={(openMenu || closingMenu)!}
                    onCategoryClick={(cat) => handleCategoryClick((openMenu || closingMenu)!, cat)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
